import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { Screen, ScreenHeader } from "../components/AppShell";
import { LanguageToggle } from "../components/LanguageToggle";
import { Button, Skeleton, StateMessage } from "../components/ui";
import { Icon } from "../components/Icon";
import { useFacets, useTaxonomy } from "../api/queries";
import { haptic } from "../telegram/haptics";
import { listItem, listStagger, pressTile, slow } from "../motion";
import { useT } from "../i18n/strings";
import { termLabel } from "../i18n/labels";
import s from "../features/browse/browse.module.css";

export default function Browse() {
  const t = useT();
  const navigate = useNavigate();
  const taxonomy = useTaxonomy();
  const facets = useFacets();

  const counts = new Map(facets.data?.types.map((row) => [row.id, row.count]));
  const busiest = Math.max(1, ...(facets.data?.types.map((row) => row.count) ?? [1]));
  const total = facets.data?.total ?? 0;

  if (taxonomy.isPending) {
    return (
      <Screen>
        <ScreenHeader sticky title={t.browse.title} />
        <div className={s.grid}>
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} height={96} radius="var(--radius-card)" />
          ))}
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <ScreenHeader sticky title={t.browse.title} actions={<LanguageToggle />} />
      <p className={s.lead}>{t.browse.lead}</p>

      {total === 0 ? (
        <StateMessage
          icon="browse"
          title={t.browse.emptyTitle}
          body={t.browse.emptyBody}
          action={
            <Button variant="secondary" icon="add" onClick={() => navigate("/new")}>
              {t.tabs.add}
            </Button>
          }
        />
      ) : (
        <motion.div className={s.grid} variants={listStagger} initial="hidden" animate="show">
          {taxonomy.data?.map((type) => {
            const count = counts.get(type.id) ?? 0;
            const label = termLabel(type);
            return (
              <motion.button
                key={type.id}
                type="button"
                variants={listItem}
                {...pressTile}
                className={[s.card, "paper", count === 0 && s.cardEmpty].filter(Boolean).join(" ")}
                onClick={() => {
                  haptic.tap();
                  navigate(`/browse/${type.id}`);
                }}
              >
                <span className={s.cardHead}>
                  <span className={s.cardLabel}>{label}</span>
                  <Icon name="forward" size={16} className={s.cardChevron} />
                </span>
                <span className={s.cardFoot}>
                  <span className={s.cardCount}>{count}</span>
                  <span className={s.cardSuffix}>{t.browse.countSuffix(count)}</span>
                </span>
                {/* The origin a scale needs is pinned per direction in the
                    stylesheet, so this fills from the leading edge either way
                    without touching layout on a grid of tiles. */}
                <motion.span
                  className={s.cardFill}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: count / busiest }}
                  transition={slow}
                />
              </motion.button>
            );
          })}
        </motion.div>
      )}
    </Screen>
  );
}
